import { hasFeature } from "./api";

// Shared definition of the two daily manager routines — Morning open
// and End of day. Data-only (no JSX) so both the routine page and the
// Manager Home progress badges read from a single source.
//
// Two kinds of step:
//   - auto  : done when the live backlog from GET /home hits zero
//             (e.g. all open tabs settled). Reflects reality, not a tick.
//   - manual: a physical/operational check the manager ticks off. Ticks
//             are stored per-day in localStorage so the routine resets
//             itself every morning.
// `count(d)` is the outstanding backlog for auto steps, or an
// informational badge for manual steps. `to` is where the step's action
// lives so the manager can jump straight there.

export const DEPT = {
  frontdesk: { tint: "#E1F5EE", ink: "#085041", k: "dept_frontdesk" },
  service:   { tint: "#E6F1FB", ink: "#0C447C", k: "dept_service" },
  kitchen:   { tint: "#FAECE7", ink: "#712B13", k: "dept_kitchen" },
  cost:      { tint: "#FAEEDA", ink: "#633806", k: "cc_section" },
  team:      { tint: "#EEEDFE", ink: "#3C3489", k: "dept_team" },
  insights:  { tint: "#EAF3DE", ink: "#27500A", k: "dept_insights" },
};

export const ROUTINES = {
  morning: {
    icon: "clock",
    title: "rt_morning_title",
    sub: "rt_morning_sub",
    steps: [
      { key: "m_rooms",    dept: "frontdesk", to: "/front-desk/housekeeping", feature: "front_desk" },
      { key: "m_arrivals", dept: "frontdesk", to: "/front-desk/reservations", feature: "front_desk" },
      { key: "m_stock",    dept: "cost",      to: "/purchasing", count: (d) => (d.attention && d.attention.low_stock) || 0 },
      { key: "m_menu",     dept: "service",   to: "/menu" },
      { key: "m_staff",    dept: "team",      to: "/assignments" },
    ],
  },
  evening: {
    icon: "file-text",
    title: "rt_evening_title",
    sub: "rt_evening_sub",
    steps: [
      { key: "e_tabs",   dept: "service",  to: "/tabs",  auto: true, count: (d) => ((d.live && d.live.open_tabs) || 0) + ((d.attention && d.attention.tabs_open_prior) || 0) },
      { key: "e_usage",  dept: "kitchen",  to: "/usage", auto: true, count: (d) => (d.attention && d.attention.pending_usage) || 0 },
      { key: "e_review", dept: "insights", to: "/adjustments", count: (d) => ((d.attention && d.attention.discounts_today) || 0) + ((d.attention && d.attention.cancellations_today) || 0) },
      { key: "e_guests", dept: "frontdesk", to: "/front-desk/guest-list", feature: "front_desk" },
      { key: "e_report", dept: "insights",  to: "/end-of-day" },
    ],
  },
};

// Local date (not UTC) so the routine's day boundary matches the venue's.
export const dayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};

const tickKey = (which, step) => `routine:${which}:${dayKey()}:${step}`;

export const isTicked = (which, step) => {
  try {
    return localStorage.getItem(tickKey(which, step)) === "1";
  } catch (e) {
    return false;
  }
};

export const setTicked = (which, step, on) => {
  try {
    if (on) localStorage.setItem(tickKey(which, step), "1");
    else localStorage.removeItem(tickKey(which, step));
  } catch (e) {
    /* storage unavailable — ticks just won't persist */
  }
};

// Auto steps are done when their backlog is empty; manual steps when
// ticked for today. Auto steps can't be evaluated until /home loads.
export function stepDone(which, step, d) {
  if (step.auto) return d ? step.count(d) === 0 : false;
  return isTicked(which, step.key);
}

// Steps whose module the venue actually has. A step tagged with a
// `feature` (e.g. front_desk) is dropped when that module isn't granted,
// so a restaurant chain without rooms never sees rooms/guest-list steps.
export function visibleSteps(routine) {
  if (!routine) return [];
  return routine.steps.filter((s) => !s.feature || hasFeature(s.feature));
}

export function routineProgress(which, d) {
  const r = ROUTINES[which];
  if (!r) return { done: 0, total: 0 };
  const steps = visibleSteps(r);
  const done = steps.filter((s) => stepDone(which, s, d)).length;
  return { done, total: steps.length };
}
